/**
 * HTML entity decoding helpers.
 *
 * Some persisted text fields (notably note-template and section-library
 * titles) arrive already HTML-encoded from upstream — e.g. a heading the
 * user typed as "Assessment & Plan" reaches the client as the literal
 * string "Assessment &amp; Plan". React renders text nodes verbatim and
 * does NOT decode entities, so those headings display the raw "&amp;"
 * (see GDN-2914; same family as the earlier "&quot;"-in-copied-notes bug).
 *
 * Decoding at the data-mapping boundary — where API titles become view
 * model titles — lets a stored "&" reach every title surface as "&"
 * without each component having to know about the encoding.
 */

// Named entities we expect to see leaking into plain-text titles. Kept small
// and explicit (rather than DOM-based) so the helper is pure and safe to run
// in any environment, including SSR and unit tests.
const NAMED_ENTITIES: Record<string, string> = {
  amp: '&',
  lt: '<',
  gt: '>',
  quot: '"',
  apos: "'",
  nbsp: ' ',
};

const ENTITY_PATTERN = /&(#x?[0-9a-f]+|[a-z]+);/gi;

/**
 * Decode the common HTML entities found in plain-text fields back to their
 * literal characters. Handles the named entities above plus numeric
 * (decimal `&#38;` and hex `&#x26;`) references.
 *
 * Decoding intentionally runs for a single pass, so a doubly encoded value
 * such as `&amp;amp;` becomes `&amp;`. Unrecognized entities are left untouched
 * so non-entity text (e.g. "R&D") is unaffected.
 */
export const decodeHtmlEntities = (value: string): string => {
  if (!value || value.indexOf('&') === -1) return value;

  return value.replace(ENTITY_PATTERN, (match, body: string) => {
    if (body[0] === '#') {
      const isHex = body[1] === 'x' || body[1] === 'X';
      const codePoint = parseInt(body.slice(isHex ? 2 : 1), isHex ? 16 : 10);
      if (Number.isNaN(codePoint) || codePoint < 0 || codePoint > 0x10ffff) {
        return match;
      }
      try {
        return String.fromCodePoint(codePoint);
      } catch {
        return match;
      }
    }

    const named = NAMED_ENTITIES[body.toLowerCase()];
    return named ?? match;
  });
};

/** Encode plain text for an HTML/XML attribute or text node exactly once. */
export const escapeHtml = (value: string): string =>
  value.replace(/[&<>"']/g, character => {
    const entities: Record<string, string> = {
      '&': '&amp;',
      '<': '&lt;',
      '>': '&gt;',
      '"': '&quot;',
      "'": '&#39;',
    };
    return entities[character];
  });
